import { atom } from 'jotai'
import { atomFamily } from 'jotai-family'
import { atomWithStorage } from 'jotai/utils'

import type { BuildExplainWidgetContextInput } from '@/features/data/components/widgets/content/amberdata/widget-explainer-context'
import type { HedgeScopeMode } from '@/features/hedge/types'
import { orderFormMinNotionalAtomFamily } from '@/features/order/order-form/lib/min-notional-atom'
import {
  ORDER_ROWS_CONFIG_KEY,
  serializeOrderRows,
} from '@/features/order/order-form/lib/persisted-order-rows'
import type { UnifiedOrderFormValues } from '@/features/order/order-form/lib/unified-order-schema'
import { STORAGE_KEYS } from '@/lib/configs/storage-config'
import store from '@/lib/configs/store-config'
import { safeStorage } from '@/lib/safe-storage'
import { rfqFormParamsAtomFamily } from '@/state/rfq'
import type { SelectedInstrument, SelectedOption } from '@/types/dashboard'
import { HedgeDeltaDenomination, SingleHedgeExecution } from '@/types/generated/algo'
import { TimeInForce } from '@/types/generated/common/trading'

// Atom to track last focused order container for PIP
export const lastFocusedOrderCardAtom = atom<string | null>(null)

// Track which OrderContainer widgets are currently mounted
export const registeredOrderContainersAtom = atom<Set<string>>(new Set<string>())

// Shared identity for the Order Form popup when NO OrderContainer widget is mounted on
// the page — every trigger site (chart BUY/SELL, watchlist bid/ask, position table, etc.)
// must resolve to this SAME id in that case, not its own widget's cardId. Order Form is
// "effectively one global widget per page" (see resolve-order-form-target.ts) — if each
// trigger widget fell back to ITS OWN cardId instead, the popup opened by one widget and
// the popup opened by another would silently be two different selection atoms, making an
// item added from one widget vanish the moment a different widget added another (the
// selection wasn't replaced — a completely different, mostly-empty atom just became the
// one being displayed). use-global-keyboard-shortcuts.ts already used this literal
// ('default') for its own popup-open shortcuts; reuse it everywhere instead of letting
// every call site improvise its own fallback id.
export const DEFAULT_ORDER_FORM_CARD_ID = 'default'

// ─── Per-card Order Form selection state (replaces linking-group fields) ───────

export interface OrderFormSelection {
  selectedInstruments: SelectedInstrument[]
  selectedOptions: SelectedOption[]
}

// nonce: prevents stale effect skip when the same price/amount is clicked consecutively
export interface OrderFormOrderbookSignal {
  price: string | null
  amount: string | null
  nonce: number
}

export const orderFormSelectionAtomFamily = atomFamily((_cardId: string) =>
  atom<OrderFormSelection>({ selectedInstruments: [], selectedOptions: [] })
)

export const orderFormOrderbookSignalAtomFamily = atomFamily((_cardId: string) =>
  atom<OrderFormOrderbookSignal>({ price: null, amount: null, nonce: 0 })
)

// Same "signal + nonce" shape as OrderFormOrderbookSignal above, for the same
// reason: useOrderFormCollapsibleConfig (service/ui-state.hook.ts) treats its
// OWN local React state as authoritative and only mirrors it OUT to
// orderFormCollapsibleConfigAtomFamily, never back in after first mount — an
// external `store.set` on that atom while the order form is already open
// would get silently clobbered by that mirror effect on the very next
// render. This signal is instead read live inside that same hook and merged
// via its own updateConfig, so it works whether the form was already open or
// not. `patch` is nulled out once consumed; `nonce` still bumps even when
// `patch` repeats so the effect re-fires on identical consecutive pushes.
export interface OrderFormEntryConditionSignal {
  patch: Partial<OrderFormCollapsibleConfig> | null
  nonce: number
}

export const orderFormEntryConditionSignalAtomFamily = atomFamily((_cardId: string) =>
  atom<OrderFormEntryConditionSignal>({ patch: null, nonce: 0 })
)

/** Form fields an external source (the Order Form's prompt bar) may pre-fill. Durations and
 *  frequencies are in seconds, matching the form's own inputs. */
export interface OrderFormFieldPatch {
  twapIntervalMinutes?: string
  twapFrequencySeconds?: string
  reduceOnly?: boolean
  postOnly?: boolean
  advancedTwapDurationSeconds?: string
  advancedTwapTotalSlices?: string
  advancedTwapBandEnabled?: boolean
  advancedTwapBandPct?: string
  advancedTwapReduceOnly?: boolean
}

// Same "signal + nonce" shape as OrderFormOrderbookSignal: react-hook-form owns these values, so
// they're pushed in through OrderFormProvider (form.setValue) rather than written to an atom.
export interface OrderFormFieldSignal {
  patch: OrderFormFieldPatch | null
  nonce: number
}

export const orderFormFieldSignalAtomFamily = atomFamily((_cardId: string) =>
  atom<OrderFormFieldSignal>({ patch: null, nonce: 0 })
)

/**
 * What the order-form prompt (the chat bar above the form) just filled in, so those parts can
 * flash once and show how the ticket changed: the leg symbols it set, and whether it set the
 * price or switched the order-type tab. `at` (Date.now()) lets a row that mounts a moment
 * later still flash, while a much later remount doesn't.
 */
export type OrderFormPromptFill = {
  nonce: number
  at: number
  legs: string[]
  price: boolean
  tab: boolean
}

export const orderFormPromptFillAtomFamily = atomFamily((_cardId: string) =>
  atom<OrderFormPromptFill>({ nonce: 0, at: 0, legs: [], price: false, tab: false })
)

// Internal popup atom. Local (non-persisted) — the popup should not reopen on reload.
const _orderFormPopupCardIdAtom = atom<string | null>(null)

/** The docked form a forced sheet shows (useOrderFormPopup's `force`); null = the sheet's own. */
export const orderFormPopupFormAtom = atom<string | null>(null)

/** Opens the shared sheet even when another OrderContainer is registered. Reserved for restoring
 * a durable chat thread whose original widget no longer exists on the current page. */
export const forceOrderFormPopupCardIdAtom = atom(null, (_get, set, value: string | null) => {
  set(orderFormPopupFormAtom, null)
  set(_orderFormPopupCardIdAtom, value)
})

// Public popup atom — when the target OrderContainer is already mounted, row injection
// stays silent and preserves its user-selected Chat/Order Form mode.
export const orderFormPopupCardIdAtom = atom(
  (get) => get(_orderFormPopupCardIdAtom),
  (get, set, value: string | null) => {
    if (value !== null) {
      const registered = get(registeredOrderContainersAtom)
      if (registered.has(value)) return
    }
    set(_orderFormPopupCardIdAtom, value)
  }
)

// Persists whether the popup sheet is showing the expanded (720px, two-column) layout.
export const orderFormPopupExpandedAtom = atomWithStorage<boolean>(
  STORAGE_KEYS.dashboard.orderFormPopupExpanded,
  false,
  safeStorage,
  { getOnInit: true }
)

// User-picked single-form account, remembered independently for each trade venue.
// Account-scoped actions (for example a position close or order modification) still
// keep their source account; this is the preferred account for new market intents.
export const lastOrderAccountByTradeVenueAtom = atomWithStorage<Record<number, string>>(
  STORAGE_KEYS.dashboard.orderFormLastAccountByTradeVenue,
  {},
  safeStorage,
  { getOnInit: true }
)

/**
 * The account the user marked as a venue's default (the account tab bar's toggle) for NEW order rows — rows added
 * without an account of their own (watchlist, history, …). An explicit choice, so it
 * outranks `lastOrderAccountByTradeVenueAtom` (an implicit trace of past tab clicks) — see
 * resolveAccount's tier order. Keyed by TradeVenue.
 */
export const defaultOrderAccountByTradeVenueAtom = atomWithStorage<Record<number, string>>(
  STORAGE_KEYS.dashboard.orderFormDefaultAccountByTradeVenue,
  {},
  safeStorage,
  { getOnInit: true }
)

// Last-synced form config for the popup card, in the same wire shape the widget config
// sync sends (`orderType`, `amountUnit`, `itemAmounts` as JSON, …). The popup is not a
// layout widget, so server-side widget-config restore never runs for it — the popup
// re-seeds its tab and amounts from this local mirror on mount instead. `cardId` records
// which card the snapshot belongs to so it is never applied to a different card.
export const orderFormPopupConfigAtom = atomWithStorage<Record<string, string> | null>(
  STORAGE_KEYS.dashboard.orderFormPopupConfig,
  null,
  safeStorage,
  { getOnInit: true }
)

// Atom to track the anchor widget rect when the popup was triggered
export const orderFormPopupAnchorAtom = atom<{
  right: number
  left: number
  top: number
  bottom?: number
} | null>(null)

// One-shot hand-off from an Amberdata widget's "explain" (sparkle) button into the popup's
// Chat tab. Written and consumed atomically with the popup open — see useOrderFormPopup and
// OrderFormSheetContent. requestId disambiguates rapid consecutive requests for the same card
// so a stale consumer effect can never clear a newer request out from under it.
export interface OrderFormPopupPendingResearch {
  requestId: string
  cardId: string
  input: BuildExplainWidgetContextInput
  label: string
}

export const orderFormPopupPendingResearchAtom = atom<OrderFormPopupPendingResearch | null>(null)

// Order form summary tabs
export enum OrderFormSummaryTab {
  Limit = 'Limit',
  Market = 'Market',
  'TWAP' = 'TWAP',
  LimitChase = 'Limit Chase',
  RFQ = 'RFQ',
  AdvancedTwap = 'Advanced TWAP',
  SingleHedge = 'Single Hedge',
  Scale = 'Scale',
}

// Per-card atom family for order type tab
export const orderFormSummaryTabAtomFamily = atomFamily((_cardId: string) =>
  atom<OrderFormSummaryTab>(OrderFormSummaryTab.Limit)
)

// Set once an action (e.g. a history row's "Add to Orderform") picks this form's tab, so the
// popup's one-shot restore of its saved tab (order-form-sheet.tsx) doesn't overwrite it.
export const orderFormSummaryTabPickedAtomFamily = atomFamily((_cardId: string) =>
  atom<boolean>(false)
)

/**
 * Whether the user has dismissed spreader mode in favor of a normal order-type
 * tab (Limit/Market/TWAP/...), even though the form's legs still carry the
 * spreaderRole tags that would otherwise put it in spreader mode — see
 * `isSpreaderMode` in use-unified-order-form.ts. Lets a Pro-tab switch away
 * from Spreader stay non-destructive (the legs/tags are left alone) so the
 * user can switch back via the "Spreader" item in the Pro dropdown, instead
 * of the old behavior of stripping the tags outright with no way back short
 * of re-adding the legs from the spread-matrix widget.
 */
export const spreaderDismissedAtomFamily = atomFamily((_cardId: string) => atom(false))

// Tracks an explicit Spreader selection from the Pro dropdown independently of
// whether the ticket already has a complete quoting/taking pair. This lets an
// empty (or single-leg) order form enter Spreader mode and build its legs there.
export const spreaderSelectedAtomFamily = atomFamily((_cardId: string) => atom(false))

// The Hedge tab's own inputs. They live beside the form rather than in it because none of them
// is a row, a size or a price; the rows only supply the default underlying and the account.
export interface SingleHedgeTabState {
  scopeMode: HedgeScopeMode
  // The underlying scope's asset. null follows the selected instrument's underlying; a picked
  // one sticks until the user picks again.
  underlying: string | null
  positionSymbols: string[]
  target: string
  denomination: HedgeDeltaDenomination
  execution: SingleHedgeExecution
  twapSeconds: string
  // null follows the selected instrument (see single-hedge-tab.ts); '' is the default perp
  // picked explicitly; anything else is a picked instrument.
  hedgeInstrument: string | null
}

export const DEFAULT_SINGLE_HEDGE_TAB_STATE: SingleHedgeTabState = {
  scopeMode: 'underlying',
  underlying: null,
  positionSymbols: [],
  target: '0',
  denomination: HedgeDeltaDenomination.HEDGE_DELTA_DENOMINATION_UNDERLYING_UNITS,
  execution: SingleHedgeExecution.SINGLE_HEDGE_EXECUTION_UNSPECIFIED,
  twapSeconds: '',
  hedgeInstrument: null,
}

export const singleHedgeTabAtomFamily = atomFamily((_cardId: string) =>
  atom<SingleHedgeTabState>(DEFAULT_SINGLE_HEDGE_TAB_STATE)
)

// Server-restored form values atom (set by use-layout-sync, consumed by order-form-context)
export type OrderFormServerValues = Partial<Omit<UnifiedOrderFormValues, 'items'>> & {
  itemAmounts?: Record<string, string> // symbol → amount, restored from server config
}

export const orderFormServerValuesAtomFamily = atomFamily((_cardId: string) =>
  atom<OrderFormServerValues | null>(null)
)

// Collapsible section config (exit-condition, entry-condition, advanced)
// Managed separately from form state via useOrderFormCollapsibleConfig
export type TriggerType = 'price' | 'schedule' | 'action_event'
export type LogicMode = 'single' | 'all' | 'any'
export type ScheduleVariant = 'delay' | 'fire_at'
export type ActionEventTargetType = 'order_id' | 'algo_id' | 'symbol'

// Condition group child shape. Stored JSON-encoded under entryConditionGroupJson.
export type GroupConditionItem =
  | {
      type: 'price'
      instrument: string
      source: string
      operator: string
      value: string
    }
  | { type: 'schedule'; variant: ScheduleVariant; value: string }
  | {
      type: 'action_event'
      eventType: string
      targetType: ActionEventTargetType
      targetValue: string
      partialFillThreshold: string
    }

export type OrderFormCollapsibleConfig = {
  priceAbove?: string
  priceBelow?: string
  // Explicit on/off for the exit bracket (TP/SL). Same tri-state semantics as
  // entryConditionEnabled: '' = unset (on when a price is present), 'true' =
  // armed, 'false' = disabled (prices kept but no exit legs attached).
  exitConditionEnabled?: string
  entryConditionInstrument?: string
  entryConditionOperator?: string
  entryConditionPrice?: string
  entryConditionTriggerType?: TriggerType
  entryConditionPriceRef?: string
  entryConditionDelaySeconds?: string
  entryConditionExpiryMinutes?: string
  // Explicit on/off for the trigger. '' = unset (treated as on when data is
  // present, for legacy configs), 'true' = armed, 'false' = disabled (inputs
  // kept but the order submits normally). See lib/trigger-active.ts.
  entryConditionEnabled?: string
  // Schedule (single-mode)
  entryConditionScheduleVariant?: ScheduleVariant
  entryConditionFireAtMs?: string
  // Action event (single-mode)
  entryConditionActionEventType?: string
  entryConditionActionTargetType?: ActionEventTargetType
  entryConditionActionTargetValue?: string
  entryConditionActionPartialFillThreshold?: string
  // Composite (ALL / ANY)
  entryConditionLogic?: LogicMode
  entryConditionGroupJson?: string // JSON-encoded GroupConditionItem[]
  // Recursive condition tree (all 12 families + nested AND/OR). When present,
  // this is authoritative; legacy fields above are migrated from on first load.
  entryConditionTreeJson?: string // JSON-encoded ConditionNode
  timeInForce?: TimeInForce
  expiresValue?: string
  expiresUnit?: string
  reduceOnly?: string
  postOnly?: string
}

export const COLLAPSIBLE_CONFIG_KEYS = [
  'priceAbove',
  'priceBelow',
  'exitConditionEnabled',
  'entryConditionInstrument',
  'entryConditionOperator',
  'entryConditionPrice',
  'entryConditionTriggerType',
  'entryConditionPriceRef',
  'entryConditionDelaySeconds',
  'entryConditionExpiryMinutes',
  'entryConditionEnabled',
  'entryConditionScheduleVariant',
  'entryConditionFireAtMs',
  'entryConditionActionEventType',
  'entryConditionActionTargetType',
  'entryConditionActionTargetValue',
  'entryConditionActionPartialFillThreshold',
  'entryConditionLogic',
  'entryConditionGroupJson',
  'entryConditionTreeJson',
  'timeInForce',
  'expiresValue',
  'expiresUnit',
  'reduceOnly',
  'postOnly',
] as const

export const orderFormCollapsibleConfigAtomFamily = atomFamily((_cardId: string) =>
  atom<OrderFormCollapsibleConfig | null>(null)
)

// Per-card denomination preference for order rows
// 'quote': always show quote currency (e.g. USD, USDC)
// 'base': always show base currency (e.g. BTC, ETH)
// 'auto': inverse instruments default to quote, linear to base
export type OrderFormDenomPreference = 'quote' | 'base' | 'auto'

export const orderFormDenomPreferenceAtomFamily = atomFamily((_cardId: string) =>
  atom<OrderFormDenomPreference>('auto')
)

// Bumped each time this card's form successfully submits an order (any tab: limit/market,
// TWAP, chase, trigger, spreader, hedge, RFQ). The prompt bar watches it to start a fresh
// chat session, so the executed draft's context doesn't leak into the next order.
export const orderFormExecutedCountAtomFamily = atomFamily((_cardId: string) => atom(0))

/**
 * The Order Form widget's seeded row: the instrument its link group's focused tab put on an
 * untouched ticket (useOrderFormLinkedSeed). While set, and the ticket is still just that one
 * row, the row follows the group's tab. A user edit or a row coming in from elsewhere makes
 * the ticket manual (null) — see markOrderFormManual.
 */
export type OrderFormSeededRow = { exchange: number; symbol: string }
export const orderFormSeededRowAtomFamily = atomFamily((_cardId: string) =>
  atom<OrderFormSeededRow | null>(null)
)

/** The ticket is the user's now: its row stops following the link group's tab. */
export function markOrderFormManual(cardId: string) {
  if (store.get(orderFormSeededRowAtomFamily(cardId)) === null) return
  store.set(orderFormSeededRowAtomFamily(cardId), null)
}

// Per-card setting: whether this widget should auto-open as a Document Picture-in-Picture
// window when the tab is hidden while it was the last-focused chat/order-form widget.
export const orderFormPipEnabledAtomFamily = atomFamily((_cardId: string) => atom<boolean>(false))

// Per-card setting: AI prompt field visibility. Existing widgets keep the prompt visible.
export const orderFormShowAiTextfieldAtomFamily = atomFamily((_cardId: string) =>
  atom<boolean>(true)
)

// Server-config keys for order-form fields restored into orderFormServerValuesAtomFamily.
// Shared with widget-config-restore so the duplicate-config builder below stays in sync.
export const FORM_CONFIG_KEYS = [
  'globalAmount',
  'amountUnit',
  'twapIntervalMinutes',
  'twapFrequencySeconds',
  'twapFrequencyMinSeconds',
  'twapFrequencyMaxSeconds',
  'twapFrequencyMode',
  'twapOrderSize',
  'twapOrderSizeMin',
  'twapOrderSizeMax',
  'twapOrderSizeMode',
] as const

/**
 * Copies per-card order form settings (summary tab, form values, RFQ params, etc.)
 * from one card to another. Used when duplicating a chat-order-form widget.
 */
export function duplicateOrderAtoms(sourceId: string, newId: string) {
  store.set(
    orderFormSummaryTabAtomFamily(newId),
    store.get(orderFormSummaryTabAtomFamily(sourceId))
  )
  store.set(
    orderFormServerValuesAtomFamily(newId),
    store.get(orderFormServerValuesAtomFamily(sourceId))
  )
  store.set(
    orderFormCollapsibleConfigAtomFamily(newId),
    store.get(orderFormCollapsibleConfigAtomFamily(sourceId))
  )
  store.set(
    orderFormDenomPreferenceAtomFamily(newId),
    store.get(orderFormDenomPreferenceAtomFamily(sourceId))
  )
  store.set(
    orderFormPipEnabledAtomFamily(newId),
    store.get(orderFormPipEnabledAtomFamily(sourceId))
  )
  store.set(
    orderFormShowAiTextfieldAtomFamily(newId),
    store.get(orderFormShowAiTextfieldAtomFamily(sourceId))
  )
  store.set(rfqFormParamsAtomFamily(newId), store.get(rfqFormParamsAtomFamily(sourceId)))
  store.set(singleHedgeTabAtomFamily(newId), store.get(singleHedgeTabAtomFamily(sourceId)))
  store.set(orderFormSelectionAtomFamily(newId), store.get(orderFormSelectionAtomFamily(sourceId)))
  store.set(
    orderFormOrderbookSignalAtomFamily(newId),
    store.get(orderFormOrderbookSignalAtomFamily(sourceId))
  )
}

/**
 * Builds the server-config payload (same shape `useWidgetConfigSync` writes and
 * `widget-config-restore`'s `restoreOrderForm` reads back) for the order-form settings
 * `duplicateOrderAtoms` copies locally, so the duplicate survives a page reload.
 */
export function buildDuplicateOrderConfig(sourceId: string): Record<string, string> {
  const config: Record<string, string> = {
    orderType: store.get(orderFormSummaryTabAtomFamily(sourceId)),
    denomPreference: store.get(orderFormDenomPreferenceAtomFamily(sourceId)),
    pipEnabled: String(store.get(orderFormPipEnabledAtomFamily(sourceId))),
    showAiTextfield: String(store.get(orderFormShowAiTextfieldAtomFamily(sourceId))),
  }

  const formValues = store.get(orderFormServerValuesAtomFamily(sourceId)) as
    | (Record<string, string | undefined> & { itemAmounts?: Record<string, string> })
    | null
  if (formValues) {
    FORM_CONFIG_KEYS.forEach((key) => {
      const value = formValues[key]
      if (value !== undefined) config[key] = value
    })
    if (formValues.itemAmounts) config.itemAmounts = JSON.stringify(formValues.itemAmounts)
  }

  const selection = store.get(orderFormSelectionAtomFamily(sourceId))
  if (selection.selectedOptions.length + selection.selectedInstruments.length > 0) {
    config[ORDER_ROWS_CONFIG_KEY] = serializeOrderRows(selection)
  }

  const collapsible = store.get(orderFormCollapsibleConfigAtomFamily(sourceId))
  if (collapsible) {
    COLLAPSIBLE_CONFIG_KEYS.forEach((key) => {
      const value = collapsible[key]
      if (value) config[key] = String(value)
    })
  }

  return config
}

export function cleanupOrderAtoms(cardId: string) {
  orderFormSummaryTabAtomFamily.remove(cardId)
  orderFormSummaryTabPickedAtomFamily.remove(cardId)
  spreaderDismissedAtomFamily.remove(cardId)
  spreaderSelectedAtomFamily.remove(cardId)
  orderFormServerValuesAtomFamily.remove(cardId)
  orderFormCollapsibleConfigAtomFamily.remove(cardId)
  optimisticOrderResultAtomFamily.remove(cardId)
  orderFormDenomPreferenceAtomFamily.remove(cardId)
  orderFormPipEnabledAtomFamily.remove(cardId)
  orderFormExecutedCountAtomFamily.remove(cardId)
  orderFormSeededRowAtomFamily.remove(cardId)
  orderFormShowAiTextfieldAtomFamily.remove(cardId)
  rfqFormParamsAtomFamily.remove(cardId)
  singleHedgeTabAtomFamily.remove(cardId)
  orderFormSelectionAtomFamily.remove(cardId)
  orderFormOrderbookSignalAtomFamily.remove(cardId)
  orderFormEntryConditionSignalAtomFamily.remove(cardId)
  orderFormFieldSignalAtomFamily.remove(cardId)
  orderFormMinNotionalAtomFamily.remove(cardId)
  orderFormPromptFillAtomFamily.remove(cardId)
}

// ─── Optimistic order clear ───────────────────────────────────────────────────

export type OptimisticOrderResult = 'success' | 'failure'

export type OptimisticOrderSignal = {
  submissionId: string
  result: OptimisticOrderResult
}

export type OptimisticOrderTarget = {
  accountId: string
  symbol: string
}

/** Composite key for a (accountId, symbol) pair — a batch submission can carry the same
 * symbol under different accounts, and a bare symbol can't disambiguate which account's
 * fill/error notification a given pending target belongs to. Keying `resolved` and
 * `pendingOptimisticSymbolsAtom` by this instead of bare symbol keeps cross-account
 * submissions (in one batch, or concurrently from different cards) from resolving each
 * other's pending state. */
export const targetKey = (accountId: string, symbol: string) => `${accountId}:${symbol}`

export type PendingOptimisticOrder = {
  submissionId: string
  cardId: string
  targets: OptimisticOrderTarget[]
  resolved: Record<string, OptimisticOrderResult>
  snapshotKey: string
  submittedAt: number
  /**
   * Anything came back for this submission — an order response of any status, or any algo
   * lifecycle event (a running TWAP stays unresolved until it completes). Distinguishes a
   * venue that answered from one that went silent (outage), without changing resolution.
   */
  acknowledged?: boolean
}

export const pendingOptimisticOrdersAtom = atom<Record<string, PendingOptimisticOrder>>({})

export const pendingOptimisticSymbolsAtom = atom<Record<string, string[]>>({})

export const optimisticOrderResultAtomFamily = atomFamily((_cardId: string) =>
  atom<OptimisticOrderSignal | null>(null)
)

export type PendingRfqOrder = {
  submissionId: string
  cardId: string
  submittedAt: number
  // Filled in once `create_rfq` resolves (manual mode only) — lets a later async
  // rejection notification (matched by submissionId/correlationId) find the
  // account-wide RFQ popup to dismiss. See GlobalRfqPopups / activeRfqPopupIdsAtom
  // in state/rfq.ts.
  rfqId?: string
}

export const pendingRfqOrdersAtom = atom<Record<string, PendingRfqOrder>>({})
